import React, { useMemo } from 'react';
import {
  Card,
  Typography,
  Box,
  Chip,
  Skeleton,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Timeline as ChartIcon,
  TrendingDown as TrendIcon,
} from '@mui/icons-material';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import type { AppRelease } from '../../types/AppMobileTypes';

interface ReleaseFrequencyChartProps {
  releases: AppRelease[];
  loading?: boolean;
}

interface DayData {
  date: string;
  label: string;
  rilasci: number;
  tendenza: number | null;
}

function buildChartData(releases: AppRelease[]): DayData[] {
  if (releases.length === 0) return [];

  const dateCounts = new Map<string, number>();
  for (const r of releases) {
    const ts = r.published_at || r.created_at;
    if (!ts) continue;
    const d = ts.toDate ? ts.toDate() : new Date(ts as any);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    dateCounts.set(key, (dateCounts.get(key) || 0) + 1);
  }

  if (dateCounts.size === 0) return [];

  const sortedDates = Array.from(dateCounts.keys()).sort();
  const startDate = new Date(sortedDates[0] + 'T00:00:00');
  const endDate = new Date(sortedDates[sortedDates.length - 1] + 'T00:00:00');

  const allDays: { date: string; rilasci: number }[] = [];
  const current = new Date(startDate);
  while (current <= endDate) {
    const key = `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}-${String(current.getDate()).padStart(2, '0')}`;
    allDays.push({ date: key, rilasci: dateCounts.get(key) || 0 });
    current.setDate(current.getDate() + 1);
  }

  const windowSize = Math.min(allDays.length <= 14 ? 3 : 7, allDays.length);
  const result: DayData[] = allDays.map((day, i) => {
    let tendenza: number | null = null;
    if (i >= windowSize - 1) {
      let sum = 0;
      for (let j = i - windowSize + 1; j <= i; j++) {
        sum += allDays[j].rilasci;
      }
      tendenza = Math.round((sum / windowSize) * 100) / 100;
    }

    const d = new Date(day.date + 'T00:00:00');
    const label = `${d.getDate()} ${d.toLocaleDateString('it-IT', { month: 'short' })}`;

    return { date: day.date, label, rilasci: day.rilasci, tendenza };
  });

  return result;
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  const rilasci = payload.find((p: any) => p.dataKey === 'rilasci');
  const tendenza = payload.find((p: any) => p.dataKey === 'tendenza');
  return (
    <Box
      sx={{
        bgcolor: 'rgba(30,30,30,0.95)',
        backdropFilter: 'blur(8px)',
        borderRadius: '10px',
        px: 2,
        py: 1.25,
        boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
        border: '1px solid rgba(255,255,255,0.08)',
        minWidth: 140,
      }}
    >
      <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 11, mb: 0.5, letterSpacing: '0.03em' }}>
        {label}
      </Typography>
      {rilasci && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: '#4caf50' }} />
          <Typography sx={{ color: 'white', fontSize: 13, fontWeight: 600 }}>
            {rilasci.value} {rilasci.value === 1 ? 'rilascio' : 'rilasci'}
          </Typography>
        </Box>
      )}
      {tendenza && tendenza.value != null && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
          <Box sx={{ width: 8, height: 2, borderRadius: '1px', bgcolor: '#ff9800' }} />
          <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 12 }}>
            Tendenza: {tendenza.value.toFixed(1)}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export const ReleaseFrequencyChart: React.FC<ReleaseFrequencyChartProps> = ({
  releases,
  loading,
}) => {
  const theme = useTheme();
  const chartData = useMemo(() => buildChartData(releases), [releases]);

  const stats = useMemo(() => {
    if (chartData.length === 0) return null;
    const totalRilasci = chartData.reduce((s, d) => s + d.rilasci, 0);
    const giorniConRilascio = chartData.filter(d => d.rilasci > 0).length;
    const giorniTotali = chartData.length;
    const mediaGiornaliera = giorniTotali > 0 ? totalRilasci / giorniTotali : 0;

    const ultimiValori = chartData.slice(-7).filter(d => d.tendenza != null);
    const primiValori = chartData.filter(d => d.tendenza != null).slice(0, 7);
    let trend: 'down' | 'up' | 'stable' = 'stable';
    if (ultimiValori.length > 0 && primiValori.length > 0) {
      const mediaUltimi = ultimiValori.reduce((s, d) => s + (d.tendenza || 0), 0) / ultimiValori.length;
      const mediaPrimi = primiValori.reduce((s, d) => s + (d.tendenza || 0), 0) / primiValori.length;
      if (mediaUltimi < mediaPrimi * 0.8) trend = 'down';
      else if (mediaUltimi > mediaPrimi * 1.2) trend = 'up';
    }

    return { totalRilasci, giorniConRilascio, giorniTotali, mediaGiornaliera, trend };
  }, [chartData]);

  return (
    <Card
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 3,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: 3,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          bgcolor: alpha('#1b1b1b', 0.03),
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <ChartIcon sx={{ fontSize: 20, color: '#4caf50' }} />
        <Typography variant="subtitle2" sx={{ flex: 1, letterSpacing: '0.02em' }}>
          Frequenza rilasci
        </Typography>
        {stats && stats.trend === 'down' && (
          <Chip
            icon={<TrendIcon sx={{ fontSize: '15px !important', color: '#4caf50 !important' }} />}
            label="In calo"
            size="small"
            sx={{
              height: 22,
              fontSize: 11,
              fontWeight: 600,
              bgcolor: alpha('#4caf50', 0.1),
              color: '#2e7d32',
            }}
          />
        )}
        {stats && stats.trend === 'up' && (
          <Chip
            label="In aumento"
            size="small"
            sx={{
              height: 22,
              fontSize: 11,
              fontWeight: 600,
              bgcolor: alpha('#ff9800', 0.1),
              color: '#e65100',
            }}
          />
        )}
      </Box>

      <Box sx={{ px: 3, pt: 2.5, pb: 2 }}>
        {loading ? (
          <Box>
            <Box sx={{ display: 'flex', gap: 4, mb: 2 }}>
              {[1, 2, 3].map(i => (
                <Skeleton key={i} variant="text" width={80} height={36} />
              ))}
            </Box>
            <Skeleton variant="rectangular" height={220} sx={{ borderRadius: 2 }} />
          </Box>
        ) : chartData.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 5 }}>
            <Typography variant="body2" color="text.secondary">
              Nessun dato disponibile per il grafico
            </Typography>
          </Box>
        ) : (
          <>
            {stats && (
              <Box sx={{ display: 'flex', gap: { xs: 2, sm: 4 }, mb: 2.5, flexWrap: 'wrap' }}>
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block' }}
                  >
                    Totale rilasci
                  </Typography>
                  <Typography variant="h5" fontWeight={800} sx={{ color: '#4caf50', lineHeight: 1.2, fontVariantNumeric: 'tabular-nums' }}>
                    {stats.totalRilasci}
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block' }}
                  >
                    Giorni con rilascio
                  </Typography>
                  <Typography variant="h5" fontWeight={800} sx={{ lineHeight: 1.2, fontVariantNumeric: 'tabular-nums' }}>
                    {stats.giorniConRilascio}
                    <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
                      / {stats.giorniTotali}
                    </Typography>
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block' }}
                  >
                    Media giornaliera
                  </Typography>
                  <Typography variant="h5" fontWeight={800} sx={{ lineHeight: 1.2, fontVariantNumeric: 'tabular-nums' }}>
                    {stats.mediaGiornaliera.toFixed(1)}
                  </Typography>
                </Box>
              </Box>
            )}

            <Box sx={{ width: '100%', height: 240 }}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                  data={chartData}
                  margin={{ top: 4, right: 4, left: -20, bottom: 0 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="rgba(0,0,0,0.06)"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 11, fill: '#999' }}
                    tickLine={false}
                    axisLine={{ stroke: 'rgba(0,0,0,0.08)' }}
                    interval={Math.max(0, Math.floor(chartData.length / 12))}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: '#999' }}
                    tickLine={false}
                    axisLine={false}
                    allowDecimals={false}
                  />
                  <Tooltip content={<CustomTooltip />} cursor={{ fill: alpha(theme.palette.primary.main, 0.06) }} />
                  <Bar
                    dataKey="rilasci"
                    fill={theme.palette.primary.main}
                    radius={[4, 4, 0, 0]}
                    maxBarSize={32}
                    fillOpacity={0.85}
                  />
                  <Line
                    dataKey="tendenza"
                    type="monotone"
                    stroke="#ff9800"
                    strokeWidth={2.5}
                    dot={false}
                    strokeDasharray="0"
                    connectNulls={false}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mt: 1.5, justifyContent: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: '#4caf50', opacity: 0.85 }} />
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                  Rilasci / giorno
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box sx={{ width: 16, height: 2.5, borderRadius: '1px', bgcolor: '#ff9800' }} />
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                  Tendenza (media mobile)
                </Typography>
              </Box>
            </Box>
          </>
        )}
      </Box>
    </Card>
  );
};
